'use client'

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import type { PropsWithChildren } from 'react'
import {
  AFFILIATE_BANNER_ENABLED,
  AFFILIATE_COOKIE_NAME,
  clearAffiliateCodeStorage,
  getStoredAffiliateCode,
  getStoredUtmParams,
  parseAffiliateCodeFromParams,
  parseUtmParams,
  sanitizeAffiliateCode,
  setAffiliateCodeStorage,
  storeUtmParams,
} from '@/lib/affiliate'

type AffiliateContextValue = {
  affiliateCode: string | null
  setAffiliateCode: (code: string | null) => void
  clearAffiliateCode: () => void
  utmParams: Record<string, string>
}

const AffiliateContext = createContext<AffiliateContextValue | undefined>(undefined)

const BANNER_DISMISSED_KEY = `${AFFILIATE_COOKIE_NAME}_banner_dismissed_for`

type AffiliateProviderProps = PropsWithChildren<{
  initialAffiliateCode?: string | null
  initialUtmParams?: Record<string, string> | null
}>

export function AffiliateProvider({ children, initialAffiliateCode = null, initialUtmParams = null }: AffiliateProviderProps) {
  const [affiliateCode, setAffiliateCodeState] = useState<string | null>(initialAffiliateCode)
  const [utmParams, setUtmParams] = useState<Record<string, string>>(initialUtmParams ?? {})
  const [bannerDismissed, setBannerDismissed] = useState(false)

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchSignature = useMemo(() => searchParams?.toString() ?? '', [searchParams])

  useEffect(() => {
    if (!affiliateCode) {
      const storedCode = getStoredAffiliateCode()
      if (storedCode) {
        setAffiliateCodeState(storedCode)
      }
    }
  }, [affiliateCode])

  useEffect(() => {
    const params = new URLSearchParams(searchSignature)

    const fromQuery = parseAffiliateCodeFromParams(params)
    if (fromQuery) {
      if (fromQuery !== affiliateCode) {
        setAffiliateCodeStorage(fromQuery)
        setAffiliateCodeState(fromQuery)
      }
      setBannerDismissed(false)
    }

    const capturedUtm = parseUtmParams(params)
    if (Object.keys(capturedUtm).length > 0) {
      setUtmParams((prev) => {
        const merged = { ...prev, ...capturedUtm }
        const prevEntries = Object.entries(prev)
        const mergedEntries = Object.entries(merged)
        const hasChanged =
          mergedEntries.length !== prevEntries.length ||
          mergedEntries.some(([key, value]) => prev[key] !== value)
        if (hasChanged) {
          storeUtmParams(merged)
          return merged
        }
        return prev
      })
    } else {
      setUtmParams((prev) => {
        if (Object.keys(prev).length > 0) {
          return prev
        }
        const stored = getStoredUtmParams()
        if (Object.keys(stored).length > 0) {
          return stored
        }
        return prev
      })
    }
  }, [affiliateCode, pathname, searchSignature])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!affiliateCode) {
      setBannerDismissed(false)
      return
    }
    try {
      const dismissedFor = window.sessionStorage.getItem(BANNER_DISMISSED_KEY)
      setBannerDismissed(dismissedFor === affiliateCode)
    } catch {
      setBannerDismissed(false)
    }
  }, [affiliateCode])

  const handleSetAffiliateCode = (value: string | null) => {
    if (!value) {
      clearAffiliateCodeStorage()
      setAffiliateCodeState(null)
      setBannerDismissed(false)
      return
    }
    const sanitized = sanitizeAffiliateCode(value)
    if (!sanitized) return
    setAffiliateCodeStorage(sanitized)
    setAffiliateCodeState(sanitized)
    setBannerDismissed(false)
  }

  const handleClearAffiliateCode = () => {
    clearAffiliateCodeStorage()
    setAffiliateCodeState(null)
    setBannerDismissed(false)
  }

  const handleDismissBanner = () => {
    setBannerDismissed(true)
    if (typeof window !== 'undefined' && affiliateCode) {
      try {
        window.sessionStorage.setItem(BANNER_DISMISSED_KEY, affiliateCode)
      } catch {
        /* ignore storage errors */
      }
    }
  }

  const contextValue = useMemo<AffiliateContextValue>(() => ({
    affiliateCode,
    setAffiliateCode: handleSetAffiliateCode,
    clearAffiliateCode: handleClearAffiliateCode,
    utmParams,
  }), [affiliateCode, utmParams])

  const showBanner = AFFILIATE_BANNER_ENABLED && Boolean(affiliateCode) && !bannerDismissed

  return (
    <AffiliateContext.Provider value={contextValue}>
      {showBanner && affiliateCode ? (
        <AffiliateBanner code={affiliateCode} onClose={handleDismissBanner} />
      ) : null}
      {children}
    </AffiliateContext.Provider>
  )
}

function AffiliateBanner({ code, onClose }: { code: string; onClose: () => void }) {
  return (
    <div className="bg-emerald-600 text-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 text-sm">
        <span>
          You&apos;re using affiliate code <strong className="font-semibold">{code}</strong> — purchases will reward this partner.
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-4 rounded border border-white/50 px-2 py-1 text-xs font-medium text-white hover:bg-white/10"
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

export function useAffiliate(): AffiliateContextValue {
  const context = useContext(AffiliateContext)
  if (!context) {
    throw new Error('useAffiliate must be used within an AffiliateProvider')
  }
  return context
}
